// ENG-2277 (HTML Forms): the editor shows a Form on its own page and in the fonts it prints in, so
// what the user edits is what the PDF prints. Lengths here are CSS inches and px, which a Form's PDF
// reproduces exactly (it prints at 96 dpi, see form-print.utils.ts).
import type { Editor } from "tinymce";

import { misc, primary } from "app/themes/colors";
import {
  FORM_CELL_PADDING_PX,
  FORM_LINE_HEIGHT,
  FormPageSize,
  FormSettings,
  TABLE_LINE_COLOR
} from "components/pdf-generation-v3/form-print.utils";
import {
  FORM_FONT_FAMILIES,
  PDF_FONT_FACES
} from "components/pdf-generation-v3/pdf-fonts";
import type { SanitizeProfile } from "utils/sanitize-html.util";

// react-pdf's page sizes (Letter 612 x 792pt, A4 595.28 x 841.89pt), in inches.
const PAGE_SIZE_IN: Record<FormPageSize, { width: number; height: number }> = {
  letter: { width: 8.5, height: 11 },
  a4: { width: 8.268, height: 11.693 }
};

// The guide drawn for a cell edge that does not print. Solid lines are the ones that print.
const GUIDE_LINE = "1px dashed #c0c7d0";
const PRINTED_LINE = `1px solid ${TABLE_LINE_COLOR}`;

const fontFaces = () =>
  PDF_FONT_FACES.map(
    ({ family, src, weight, style }) =>
      `@font-face { font-family: "${family}"; src: url("${src}") format("truetype"); font-weight: ${weight}; font-style: ${style}; }`
  ).join("\n");

// A paragraph has no bottom margin and a list has the PDF sheet's margins (FORM_PDF_CSS_STYLE), so
// whatever follows one starts where it prints. Two paragraphs still sit 10px apart.
const pageStyle = ({
  page_size,
  margins_in: { top, right, bottom, left }
}: FormSettings) => {
  const { width, height } = PAGE_SIZE_IN[page_size];
  return `
html { background: #eceff1; }
body {
  box-sizing: border-box;
  width: ${width}in;
  min-height: ${height}in;
  margin: 16px auto;
  padding: ${top}in ${right}in ${bottom}in ${left}in;
  background: #ffffff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  font-family: ${FORM_FONT_FAMILIES.lato};
  font-size: 14px;
  line-height: ${FORM_LINE_HEIGHT};
  color: #000000;
}
body p { margin: 10px 0 0; }
body ul, body ol { margin: 6px 0 18px; }`;
};

// A table's own cells, in any of its sections or written with no section at all. Child
// combinators keep one table's lines off a table nested inside one of its cells.
const ownCells = (table: string, row = "tr") =>
  ["> tbody >", "> thead >", "> tfoot >", ">"]
    .flatMap((section) =>
      ["td", "th"].map(
        (cell) => `.mce-content-body ${table} ${section} ${row} > ${cell}`
      )
    )
    .join(", ");

// Mirrors the PDF: each table's data-lines decides which edges print, and an author's own cell
// border wins (it is an inline style). The `.mce-content-body` prefix outranks TinyMCE's own dashed
// table guides. Purchase agreements use these rules too, without the rest of Form mode.
const TABLE_LINES_EDITOR_STYLE = `
${ownCells("table[data-lines]")} { border: ${GUIDE_LINE}; }
${ownCells('table[data-lines="all"]')} { border: ${PRINTED_LINE}; }
.mce-content-body table[data-lines="outside"] { border: ${PRINTED_LINE}; }
${ownCells('table[data-lines="rows"]', "tr:not(:last-child)")} { border-bottom: ${PRINTED_LINE}; }`;

// A Form's cell without its own padding, as in the PDF. The class on body outranks TinyMCE's own
// `table:not([cellpadding]) td` padding; a cell's inline padding still wins.
const CELL_STYLE = `
body.mce-content-body table td, body.mce-content-body table th { padding: ${FORM_CELL_PADDING_PX.vertical}px ${FORM_CELL_PADDING_PX.horizontal}px; }`;

// A box is block-level and never grows, as in the PDF (adjustBoxes). Its outline is a guide only.
// The box decides its image's size, so these win over the image's own inline sizes; a signature
// sits on its line and is only ever made smaller to fit, never larger. Purchase agreements use
// these rules too, without the rest of Form mode.
const BOX_EDITOR_STYLE = `
div[data-box] { display: block; box-sizing: border-box; max-width: 100%; overflow: hidden; outline: ${GUIDE_LINE}; }
div[data-box]:not([data-box-line="false"]) { border-bottom: 1px solid #000000; }
div[data-box] img { width: 100% !important; height: 100% !important; max-width: 100% !important; max-height: 100% !important; object-fit: contain; object-position: left top; }
div[data-box="signature"] img { object-fit: scale-down; object-position: left bottom; }
div[data-box="signature"] .sign-button { max-height: 100%; margin: 0; padding: 0 8px; font-size: 11px; line-height: 1.2; }`;

// SN-19949: a blank the print dialog made a field (blank-fields.utils.ts). The blank's own width, the line under it
// and a tint say "type here"; none of it prints (printBlankFields). "Full" shows when the text can shrink no more,
// and a date field has a tab above it that opens the date picker, so the whole line stays free for the date.
const BLANK_FIELD_EDITOR_STYLE = `
body.mce-content-body [data-blank-field] { display: inline-block; position: relative; box-sizing: border-box; vertical-align: baseline; white-space: nowrap; border-bottom: 1px solid #000000; background: ${misc.veryLightGreen}; }
body.mce-content-body [data-blank-text] { display: inline-block; min-width: 100%; max-width: 100%; overflow: hidden; vertical-align: bottom; outline: none; white-space: pre; }
body.mce-content-body [data-blank-field][data-mce-selected], body.mce-content-body [data-blank-field] [data-blank-text]:focus, body.mce-content-body [data-blank-field] [data-blank-text]:hover { outline: none; }
body.mce-content-body [data-blank-field]:focus-within { box-shadow: 0 2px 0 ${primary[300]}; }
body.mce-content-body [data-blank-field].blank-full { box-shadow: 0 2px 0 ${misc.warningText}; }
body.mce-content-body [data-blank-field].blank-full::after { content: "Full"; position: absolute; right: 0; bottom: 100%; padding: 1px 5px; border-radius: 3px; background: ${misc.warningText}; color: #ffffff; font: 700 10px/1.3 sans-serif; }
body.mce-content-body [data-blank-date-tab] { position: absolute; left: 0; bottom: 100%; padding: 0 4px; border: 1px solid ${primary[300]}; border-bottom: 0; border-radius: 3px 3px 0 0; background: ${misc.veryLightGreen}; font-size: 11px; line-height: 1.3; cursor: pointer; }
body.mce-content-body [data-blank-date-tab] input { position: absolute; left: 0; bottom: 0; width: 1px; height: 1px; opacity: 0; pointer-events: none; }`;

export type FormModeEditorConfig = {
  content_style: string;
  font_family_formats: string;
  table_default_attributes: Record<string, string>;
  // Not a TinyMCE option: the profile the field's paste hook sanitizes with.
  sanitizeProfile: SanitizeProfile;
};

export const buildFormModeEditorConfig = (
  formSettings: FormSettings
): FormModeEditorConfig => ({
  content_style: [
    fontFaces(),
    pageStyle(formSettings),
    CELL_STYLE,
    TABLE_LINES_EDITOR_STYLE,
    BOX_EDITOR_STYLE,
    BLANK_FIELD_EDITOR_STYLE
  ].join("\n"),
  font_family_formats: [
    `Arial=${FORM_FONT_FAMILIES.sans}`,
    `Times New Roman=${FORM_FONT_FAMILIES.serif}`,
    `Courier New=${FORM_FONT_FAMILIES.mono}`,
    `Lato=${FORM_FONT_FAMILIES.lato}`
  ].join("; "),
  table_default_attributes: { "data-lines": formSettings.default_table_lines },
  sanitizeProfile: "form"
});

/**
 * The Digital Form editor's toolbar (ENG-2277): formatting first, then what a form is made of —
 * fields, signature lines, tables, images, page breaks — and Edit HTML at the end. Undo and redo
 * stay first, as on every editor.
 */
export const FORM_EDITOR_TOOLBAR = [
  "undo redo",
  "blocks fontfamily fontsizeinput",
  "bold italic underline forecolor removeformat",
  "align bullist numlist",
  "formfield formsignature table image pagebreak",
  "formhtml"
].join(" | ");

/** The labelled buttons FORM_EDITOR_TOOLBAR names. The page decides what each picker shows. */
export const addFormEditorButtons = (
  editor: Editor,
  actions: () =>
    | { onInsertField: () => void; onSignatureLine: () => void }
    | undefined
) => {
  editor.ui.registry.addButton("formfield", {
    text: "Insert field",
    icon: "addtag",
    tooltip: "Insert a field that fills in from the record",
    onAction: () => actions()?.onInsertField()
  });
  editor.ui.registry.addButton("formsignature", {
    text: "Signature line",
    tooltip: "Insert a box to sign in, with a line under it",
    onAction: () => actions()?.onSignatureLine()
  });
  editor.ui.registry.addButton("formhtml", {
    text: "Edit HTML",
    tooltip: "Edit the form's HTML",
    onAction: () => editor.execCommand("mceCodeEditor")
  });
};

export const TAG_WITH_OPTIONS_ATTR = "data-mce-param-tag";
const TAG_WITH_OPTIONS = /(\{\{\s*[^{}()]+\([^{}]*\)\s*\}\})/;

type AstNode = {
  value?: string;
  parent?: AstNode | null;
  attr: (name: string, value?: string | null) => string | undefined;
  append: (node: AstNode) => AstNode;
  insert: (node: AstNode, ref: AstNode, before?: boolean) => AstNode;
  remove: () => AstNode;
  unwrap: () => void;
};
type AstNodeStatic = {
  create: (name: string, attrs?: Record<string, string>) => AstNode;
};

/**
 * A tag with options, e.g. the invoice table's {{Patient.SelectedInvoice.WithPredefinedParameters(…)}},
 * shown as a chip the way the mergetags plugin shows the others: that plugin wraps a tag only when it is
 * one of its list's exact values, so this one stayed as its code, editable, and a stray keystroke broke
 * it. Wrapped as the editor reads the content and unwrapped as it saves, so the stored HTML keeps its
 * exact text. Called from the editor's setup; the listener lives and goes with the editor.
 */
export const chipTagsWithOptions = (
  editor: Editor,
  // Which tags to wrap, asked as each piece of content is read. A Form wraps every tag with options; a template
  // editor only the invoice table, and only while its editing is switched on (invoice-table-tag.utils).
  {
    shouldWrap = () => true,
    isEnabled = () => true
  }: {
    shouldWrap?: (tag: string) => boolean;
    // Asked once per parse, before any text is looked at: off, the filter costs nothing.
    isEnabled?: () => boolean;
  } = {}
) => {
  // The parser exists from PreInit, which still comes before the first content is read.
  editor.on("PreInit", () => {
    const Node = (
      window as unknown as { tinymce?: { html: { Node: AstNodeStatic } } }
    ).tinymce?.html.Node;
    if (!Node) return;
    editor.parser.addNodeFilter("#text", (nodes) => {
      if (!isEnabled()) return;
      (nodes as unknown as AstNode[]).forEach((text) => {
        const value = text.value ?? "";
        if (
          !TAG_WITH_OPTIONS.test(value) ||
          text.parent?.attr(TAG_WITH_OPTIONS_ATTR) !== undefined
        )
          return;
        const parts = value.split(TAG_WITH_OPTIONS);
        // split() puts each captured tag at an odd index.
        const wraps = (index: number) =>
          index % 2 === 1 && shouldWrap(parts[index]);
        if (!parts.some((_, index) => wraps(index))) return;
        parts.forEach((part, index) => {
          if (!part) return;
          const piece = Node.create("#text");
          piece.value = part;
          if (!wraps(index)) {
            text.insert(piece, text, true);
            return;
          }
          const chip = Node.create("span", {
            [TAG_WITH_OPTIONS_ATTR]: "",
            contenteditable: "false"
          });
          chip.append(piece);
          text.insert(chip, text, true);
        });
        text.remove();
      });
    });
    editor.serializer.addAttributeFilter(TAG_WITH_OPTIONS_ATTR, (nodes) =>
      (nodes as unknown as AstNode[]).forEach((node) => node.unwrap())
    );
  });
};
